const MAX_PROCESS_DIMENSION = 1200;

const getSvgNaturalDimensions = async (file: File): Promise<{ width: number; height: number }> => {
  const text = await file.text();
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  const svg = doc.documentElement;
  const viewBox = svg.getAttribute('viewBox');
  if (viewBox) {
    const parts = viewBox.trim().split(/[\s,]+/);
    const w = parseFloat(parts[2]);
    const h = parseFloat(parts[3]);
    if (w > 0 && h > 0) return { width: w, height: h };
  }
  const w = parseFloat(svg.getAttribute('width') ?? '0');
  const h = parseFloat(svg.getAttribute('height') ?? '0');
  if (w > 0 && h > 0) return { width: w, height: h };
  return { width: MAX_PROCESS_DIMENSION, height: MAX_PROCESS_DIMENSION };
};

export const convertToProcessedPng = async (file: File): Promise<File> => {
  const naturalDims =
    file.type === 'image/svg+xml'
      ? await getSvgNaturalDimensions(file)
      : await new Promise<{ width: number; height: number }>((resolve, reject) => {
          const url = URL.createObjectURL(file);
          const img = new Image();
          img.onload = () => {
            URL.revokeObjectURL(url);
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
          };
          img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Image load failed'));
          };
          img.src = url;
        });

  const { width, height } = naturalDims;
  const isSvg = file.type === 'image/svg+xml';
  const minDim = Math.min(width, height);
  const maxDim = Math.max(width, height);
  // SVGs always scale to 1200px on smaller dim; raster only scales down.
  const scaleFromMin = isSvg || minDim > MAX_PROCESS_DIMENSION ? MAX_PROCESS_DIMENSION / minDim : 1;
  // If the projected larger dimension would exceed 2×MAX, clamp further.
  const scale = maxDim * scaleFromMin > MAX_PROCESS_DIMENSION * 2 ? (MAX_PROCESS_DIMENSION * 2) / maxDim : scaleFromMin;
  const outW = Math.max(1, Math.round(width * scale));
  const outH = Math.max(1, Math.round(height * scale));

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas not available'));
        return;
      }
      ctx.drawImage(img, 0, 0, outW, outH);
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Conversion failed'));
          return;
        }
        resolve(new File([blob], file.name, { type: 'image/png' }));
      }, 'image/png');
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Image load failed'));
    };
    img.src = url;
  });
};
